import { cloneDeep } from 'lodash';
import { updateElementsOrderFromZIndex } from '@/utils/elementsUtils';
import type { Object as FabricObject } from 'fabric/fabric-impl';
import type { CanvasEx } from '@/typings/canvas';
import { ValueFormat } from '@/typings/designStudio/sourceElementTypes';
import {
    type PatternConfig,
    PATTERN_DEFAULTS,
    type PatternCell,
    type CellPosition,
    type PatternElement,
    type PatternLink,
    type PatternProductService,
    type PatternReplicaService,
    type CreatePatternHandlersParams,
    type SharedRepeat,
    type SharedRepeatChange,
    type OriginTransform
} from '@/typings/customilyElements/patternElements';

/** Parse the stored patternConfig (JSON string or object) into a full config, or null. */
export const parsePatternConfig = (value: string | PatternConfig | null): PatternConfig | null => {
    if (!value) return null;
    try {
        const parsed = typeof value === 'string' ? JSON.parse(value) : value;
        if (!parsed || typeof parsed !== 'object') return null;
        return { ...PATTERN_DEFAULTS, ...(parsed as Partial<PatternConfig>) };
    } catch {
        return null;
    }
};

const halfExtent = (total: number): number => Math.max(0, Math.floor((total - 1) / 2));

/**
 * Enumerate the grid cells for a config in a deterministic, stable order,
 * excluding the origin cell (0,0). The order must match between creation and re-sync so replicas map
 * to the same cell across operations.
 */
export const enumerateCells = (config: PatternConfig): PatternCell[] => {
    const rowExtent = halfExtent(config.rows);
    const colExtent = halfExtent(config.cols);
    const cells: PatternCell[] = [];
    for (let row = -rowExtent; row <= rowExtent; row++) {
        for (let col = -colExtent; col <= colExtent; col++) {
            if (row === 0 && col === 0) continue;
            cells.push({ row, col });
        }
    }
    return cells;
};

type GroupedObject = FabricObject & {
    group?: { calcTransformMatrix: () => number[]; angle?: number; scaleX?: number; scaleY?: number };
};

/** Canvas-space center: inside an activeSelection fabric 4 reports it relative to the selection */
const getAbsoluteCenter = (object: FabricObject): { x: number; y: number } => {
    const center = object.getCenterPoint();
    const group = (object as GroupedObject).group;
    if (!group) return center;
    const [a, b, c, d, e, f] = group.calcTransformMatrix();
    return { x: a * center.x + c * center.y + e, y: b * center.x + d * center.y + f };
};

/** Canvas-space size: getScaledWidth leaves out the scale of the selection holding it */
const getAbsoluteSize = (object: FabricObject): { width: number; height: number } => {
    const group = (object as GroupedObject).group;
    return {
        width: object.getScaledWidth() * (group?.scaleX ?? 1),
        height: object.getScaledHeight() * (group?.scaleY ?? 1)
    };
};

/**
 * Canvas-space angle/scale while a rotated or scaled selection holds the object, undefined anywhere else: replicas
 *  then take its own, as they always did. Selections scale uniformly (lockUniScaling), so no skew comes out of this
 */
export const getSelectionTransform = (object: FabricObject): OriginTransform | undefined => {
    const group = (object as GroupedObject).group;
    if (!group) return undefined;
    const groupScaleX = group.scaleX ?? 1;
    const groupScaleY = group.scaleY ?? 1;
    if (!group.angle && groupScaleX === 1 && groupScaleY === 1) return undefined;
    return {
        angle: (object.angle ?? 0) + (group.angle ?? 0),
        scaleX: (object.scaleX ?? 1) * groupScaleX,
        scaleY: (object.scaleY ?? 1) * groupScaleY
    };
};

const getUnionBounds = (objects: FabricObject[]) => {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const object of objects) {
        const center = getAbsoluteCenter(object);
        const size = getAbsoluteSize(object);
        const halfWidth = size.width / 2;
        const halfHeight = size.height / 2;
        minX = Math.min(minX, center.x - halfWidth);
        minY = Math.min(minY, center.y - halfHeight);
        maxX = Math.max(maxX, center.x + halfWidth);
        maxY = Math.max(maxY, center.y + halfHeight);
    }
    return { centerX: (minX + maxX) / 2, centerY: (minY + maxY) / 2, width: maxX - minX, height: maxY - minY };
};

/** Size of several elements taken as one, what their shared spacing and offset are relative to */
export const getSetSize = (objects: FabricObject[]): { width: number; height: number } => {
    const { width, height } = getUnionBounds(objects);
    return { width, height };
};

/** Center-to-center step between adjacent cells and the origin's on-canvas size, in pixels. */
const getGridSteps = (
    origin: FabricObject,
    config: PatternConfig
): { stepX: number; stepY: number; width: number; height: number } => {
    const { width, height } = getAbsoluteSize(origin);
    return {
        width,
        height,
        stepX: width * (1 + config.spacingX),
        stepY: height * (1 + config.spacingY)
    };
};

/** A cell's center from the origin's center and size: shared by the canvas and the saved preview elements */
export const computeCellCenter = (
    origin: { centerX: number; centerY: number; width: number; height: number },
    config: PatternConfig,
    cell: PatternCell
): { left: number; top: number } => {
    const { centerX, centerY, width, height } = origin;
    const oddRowShift = cell.row % 2 !== 0 ? config.offsetX * width : 0;
    const oddColShift = cell.col % 2 !== 0 ? config.offsetY * height : 0;
    return {
        left: centerX + cell.col * width * (1 + config.spacingX) + oddRowShift,
        top: centerY + cell.row * height * (1 + config.spacingY) + oddColShift
    };
};

/** Absolute canvas center position for a single cell. */
export const computeCellPosition = (origin: FabricObject, config: PatternConfig, cell: PatternCell): CellPosition => {
    const center = getAbsoluteCenter(origin);
    const transform = getSelectionTransform(origin);
    return {
        ...cell,
        ...computeCellCenter({ centerX: center.x, centerY: center.y, ...getAbsoluteSize(origin) }, config, cell),
        ...(transform && { transform })
    };
};

/** Spacing that makes an origin of this size repeat every `distance` pixels */
export const spacingForRepeat = (size: number, distance: number): number => (size > 0 ? distance / size - 1 : 0);

// Spacing and offset are relative to each origin's size: patterns line up only when these absolute values match
const getRepeat = (origin: PatternElement, config: PatternConfig) => {
    const { stepX, stepY, width, height } = getGridSteps(origin, config);
    return {
        repeatX: stepX,
        repeatY: stepY,
        shiftX: config.offsetX * width,
        shiftY: config.offsetY * height
    };
};

const TOLERANCE_PX = 0.5;

/** What several patterns share, null where they differ (shown as Mixed) */
export const getSharedRepeat = (origins: PatternElement[]): SharedRepeat => {
    const repeats = origins.filter((o) => o.patternConfig).map((o) => getRepeat(o, o.patternConfig as PatternConfig));
    const shared = (key: keyof SharedRepeat) => {
        if (!repeats.length) return null;
        const first = repeats[0][key];
        return repeats.every((r) => Math.abs(r[key] - first) <= TOLERANCE_PX) ? first : null;
    };
    return {
        repeatX: shared('repeatX'),
        repeatY: shared('repeatY'),
        shiftX: shared('shiftX'),
        shiftY: shared('shiftY')
    };
};

/** The origin's own config for a value set on several patterns at once; what's left out keeps its current value */
export const applySharedRepeat = (
    origin: PatternElement,
    config: PatternConfig,
    change: SharedRepeatChange
): PatternConfig => {
    const { width, height } = getAbsoluteSize(origin);
    const current = getRepeat(origin, config);
    const repeatX = change.repeatX ?? current.repeatX;
    const repeatY = change.repeatY ?? current.repeatY;
    const shiftX = change.shiftX ?? current.shiftX;
    const shiftY = change.shiftY ?? current.shiftY;
    return {
        ...config,
        spacingX: spacingForRepeat(width, repeatX),
        spacingY: spacingForRepeat(height, repeatY),
        offsetX: width ? shiftX / width : 0,
        offsetY: height ? shiftY / height : 0
    };
};

export const getArtworkBounds = (canvas: CanvasEx): { width: number; height: number } => ({
    width: (canvas.getArtworkWidth() / canvas.getZoom()) * canvas.getEffectiveZoom(),
    height: (canvas.getArtworkHeight() / canvas.getZoom()) * canvas.getEffectiveZoom()
});

/**
 * Compute how many rows/cols are needed to fill the artwork: expand outward from the centered origin
 * while a cell still intersects the canvas at all; stop once the next ring is fully out of bounds.
 * A cell may cross the edge but is included as long as any part is inside.
 */
export const computeFillExtent = (
    origin: FabricObject,
    config: PatternConfig,
    canvasWidth: number,
    canvasHeight: number
): { rows: number; cols: number } => {
    const { stepX, stepY, width, height } = getGridSteps(origin, config);
    const center = getAbsoluteCenter(origin);
    // Offsets stagger odd rows/cols by a fraction of the origin size; widen the half-extent by the
    // largest possible shift so the extent still covers the staggered cells and leaves no edge gaps.
    const maxShiftX = Math.abs(config.offsetX) * width;
    const maxShiftY = Math.abs(config.offsetY) * height;
    const halfW = width / 2 + maxShiftX;
    const halfH = height / 2 + maxShiftY;

    const intersectsX = (col: number) => {
        const centerX = center.x + col * stepX;
        return centerX + halfW > 0 && centerX - halfW < canvasWidth;
    };
    const intersectsY = (row: number) => {
        const centerY = center.y + row * stepY;
        return centerY + halfH > 0 && centerY - halfH < canvasHeight;
    };

    let colExtent = 0;
    if (stepX > 0) {
        while (intersectsX(colExtent + 1) || intersectsX(-(colExtent + 1))) colExtent++;
    }
    let rowExtent = 0;
    if (stepY > 0) {
        while (intersectsY(rowExtent + 1) || intersectsY(-(rowExtent + 1))) rowExtent++;
    }

    return { rows: rowExtent * 2 + 1, cols: colExtent * 2 + 1 };
};

/** Element as loaded: patternConfig still holds the saved JSON string and replicas may carry a stroke backup */
type LoadedPatternElement = PatternElement & {
    patternConfig?: PatternConfig | string | null;
    _backupStroke?: string;
    setStroke?: (stroke: string) => void;
};

/**
 * Rebuilds the in-memory pattern state of a loaded product: only the origin's patternConfig and the links are saved,
 *  the pattern/replica flags and each replica's cell come from them. `onReplicaLinked` lets each element type finish
 *  wiring its replicas. `keepSavedLock` is for previews (step 2), the only place a replica can be unlocked.
 */
export const populatePatternLinks = (
    elementsLinks: PatternLink[],
    canvas: CanvasEx,
    onReplicaLinked?: (origin: PatternElement, replica: PatternElement) => void,
    { keepSavedLock = false }: { keepSavedLock?: boolean } = {}
) => {
    const patternLinks = elementsLinks.filter((link) => link.valueFormat === ValueFormat.Pattern);
    if (!patternLinks.length) return;

    const objects = canvas.getObjects() as LoadedPatternElement[];
    const linksByOrigin = new Map<string, PatternLink[]>();
    for (const link of patternLinks) {
        if (!linksByOrigin.has(link.sourceId)) linksByOrigin.set(link.sourceId, []);
        linksByOrigin.get(link.sourceId)!.push(link);
    }

    for (const [originId, links] of linksByOrigin) {
        const origin = objects.find((obj) => obj.uuid === originId);
        if (!origin) continue;

        const config = parsePatternConfig(origin.patternConfig ?? null);
        origin.pattern = true;
        origin.patternConfig = config;
        const cells = config ? enumerateCells(config) : [];

        links.forEach((link, index) => {
            const replica = objects.find((obj) => obj.uuid === link.targetId);
            if (!replica) return;
            replica.isPatternReplica = true;
            replica.patternOriginUuid = originId;
            if (!keepSavedLock) replica.set('locked', true);
            // Replicas never show the red "locked" border updateLockStatus may have painted
            if (replica._backupStroke !== undefined) {
                if (replica.setStroke) replica.setStroke(replica._backupStroke);
                else replica.set('stroke', replica._backupStroke);
            }
            if (cells[index]) {
                replica.patternCell = { row: cells[index].row, col: cells[index].col };
            }
            onReplicaLinked?.(origin, replica);
        });
    }
};

/** Edited in the origin but each replica's own: its lock (a preview can unlock it), layer and group */
const REPLICA_OWN_PROPS = new Set(['pattern', 'patternConfig', 'zIndex', 'locked', 'groupId', 'ungroup']);

type EditTracked = PatternElement & { editedProps?: string[] };

/** In edit mode only marked edits reach the previews: replicas take their origin's marks */
export const mirrorOriginEditsToReplicas = (origin: PatternElement, replicas: PatternElement[]) => {
    const edited = ((origin as EditTracked).editedProps ?? []).filter((prop) => !REPLICA_OWN_PROPS.has(prop));
    for (const replica of replicas) {
        if (edited.length) replica.setEditedProp(...edited);
        if (origin.editedSize) replica.editedSize = true;
    }
};

/** A saved preview text or image placeholder, as far as a pattern's layout goes */
export interface PreviewPatternEntry {
    uuid?: string;
    centerX: number;
    centerY: number;
    width: number;
    height: number;
    patternConfig?: PatternConfig | string | null;
    locked?: boolean;
    addedOnEdit?: boolean;
    [key: string]: unknown;
}

/** What tells an added replica apart from its siblings: the rest it takes from one already sized for the preview */
const ADDED_REPLICA_OWN_PROPS = ['uuid', 'id', 'name', 'zIndex', 'groupId', 'visible', 'locked'];

/** Database keys of a preview element and of its child rows, never copied: the copy is saved as new rows */
const PREVIEW_ROW_KEYS = ['TextId', 'ImagePlaceHolderId'];
const PREVIEW_CHILD_ROW_KEYS: Record<string, string> = { textures: 'textureID', colors: 'ColorId' };

const copyAsNewRow = (entry: PreviewPatternEntry): PreviewPatternEntry => {
    const copy = cloneDeep(entry);
    for (const key of PREVIEW_ROW_KEYS) delete copy[key];
    for (const [childList, key] of Object.entries(PREVIEW_CHILD_ROW_KEYS)) {
        for (const child of (copy[childList] as Record<string, unknown>[] | null | undefined) ?? []) delete child[key];
    }
    return copy;
};

/**
 * Every preview's replicas after a step 1 resize/reconfig: the only update an unopened preview gets, and in an opened
 *  one the same layout step 2 computes. Mutates `lists`: added replicas get replaced
 */
export const relayoutReplicasInPreview = (
    lists: PreviewPatternEntry[][],
    links: PatternLink[],
    originUuids: Set<string>
) => {
    const located = new Map<string, { list: PreviewPatternEntry[]; index: number }>();
    for (const list of lists) {
        list.forEach((entry, index) => {
            if (entry.uuid) located.set(entry.uuid, { list, index });
        });
    }
    const get = (uuid: string) => {
        const at = located.get(uuid);
        return at && at.list[at.index];
    };
    const linksByOrigin = new Map<string, PatternLink[]>();
    for (const link of links) {
        if (link.valueFormat !== ValueFormat.Pattern || !originUuids.has(link.sourceId)) continue;
        if (!linksByOrigin.has(link.sourceId)) linksByOrigin.set(link.sourceId, []);
        linksByOrigin.get(link.sourceId)!.push(link);
    }

    for (const [originUuid, originLinks] of linksByOrigin) {
        const origin = get(originUuid);
        const config = parsePatternConfig(origin?.patternConfig ?? null);
        // A new origin is in step 1 space as its replicas are: step 2 scales them all alike
        if (!origin || !config || origin.addedOnEdit) continue;
        const sibling = originLinks
            .map((link) => get(link.targetId))
            .find((replica) => replica && !replica.addedOnEdit && replica.locked !== false);
        const base = sibling ?? { ...origin, patternConfig: null };
        // Link order is cell order, as in populatePatternLinks
        const cells = enumerateCells(config);
        originLinks.forEach((link, index) => {
            const at = located.get(link.targetId);
            let replica = at && at.list[at.index];
            if (!at || !replica || !cells[index] || replica.locked === false) return;
            // Added in step 1: one object shared by every preview, in step 1 space until step 2 loads that preview
            if (replica.addedOnEdit) {
                const own = Object.fromEntries(ADDED_REPLICA_OWN_PROPS.map((key) => [key, replica?.[key]]));
                replica = { ...copyAsNewRow(base), ...own, addedOnEdit: false };
                at.list[at.index] = replica;
            }
            const { left, top } = computeCellCenter(origin, config, cells[index]);
            replica.centerX = left;
            replica.centerY = top;
        });
    }
};

/** Types that can originate a pattern; which image placeholders qualify is narrowed by the properties panel */
export const isPatternOriginType = (object?: { type?: string } | null): boolean =>
    object?.type === 'bounded-text' || object?.type === 'image-placeholder';

// Roles flagged on their own: a moon phase imported from a PSD has the flag but no dynamicImagesPath
const hasGeneratedContent = (object: Record<string, unknown>) =>
    !!(
        object.dynamicImagesPath ||
        object.mapConfig ||
        object.crossword ||
        object.calendar ||
        object.wordCloudConfig ||
        object.wordSearchConfig ||
        object.isMoonPhase ||
        object.isGuide ||
        object.isProductGuidelines ||
        object.isColorOptions
    );

/** Whether a pattern can be created from it: plain texts and plain image placeholders only */
export const canBePatternOrigin = (object?: { type?: string; [key: string]: unknown } | null): boolean => {
    if (!object || object.isPatternReplica) return false;
    if (object.type === 'bounded-text') return !object.textPath && !object.shapeConfig;
    // Print area and text mask target are roles a plain placeholder can take
    return (
        object.type === 'image-placeholder' &&
        !hasGeneratedContent(object) &&
        !object.isPrintArea &&
        !object.hasTextMask
    );
};

/** Replicas of an origin, in link order (the order enumerateCells assigned them). */
export const getPatternReplicaObjects = (
    canvas: CanvasEx,
    ProductService: Pick<PatternProductService, 'getPatternReplicaLinks'>,
    originUuid: string
): PatternElement[] => {
    const objects = canvas.getObjects() as PatternElement[];
    return ProductService.getPatternReplicaLinks(originUuid)
        .map((link) => objects.find((o) => o.uuid === link.targetId))
        .filter((o): o is PatternElement => !!o);
};

/** An unlocked replica (step 2) is detached from its origin until it's locked again */
export const followsOrigin = (replica: PatternElement) => replica.locked !== false;

// setTimeout and not requestAnimationFrame: a hidden tab would stop the batches
const yieldToBrowser = () => new Promise<void>((resolve) => setTimeout(resolve, 0));

// Every run finishes before a failure is reported: a cleanup after it has to see all that was added
const settleAll = async <T, R>(items: T[], run: (item: T, index: number) => R | Promise<R>): Promise<R[]> => {
    const results = await Promise.allSettled(
        items.map((item, i) => {
            try {
                return run(item, i);
            } catch (error) {
                return Promise.reject(error);
            }
        })
    );
    const failed = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');
    if (failed) throw failed.reason;
    return results.map((result) => (result as PromiseFulfilledResult<Awaited<R>>).value);
};

/** Topmost of the given objects; a tie is on top when it's drawn later */
const getTopmost = (objects: PatternElement[], canvasObjects: PatternElement[]) =>
    objects.reduce((top, object) =>
        object.zIndex > top.zIndex ||
        (object.zIndex === top.zIndex && canvasObjects.indexOf(object) > canvasObjects.indexOf(top))
            ? object
            : top
    );

export const createPatternHandlers = ({
    getCanvas,
    $scope,
    ProductService,
    getReplicaService
}: CreatePatternHandlersParams) => {
    const markEdited = (origin: PatternElement, keys: string[]) => {
        if ($scope.editMode) origin.setEditedProp(...keys);
    };

    /**
     * Runs the work in the service's batches, rendering the progress between them; null when a newer sync made
     *  this one stale. Services without batchSize run everything at once, as the text pattern always did.
     */
    const runInBatches = async <T, R>(
        items: T[],
        { batchSize, batchTimeBudgetMs }: Pick<PatternReplicaService, 'batchSize' | 'batchTimeBudgetMs'>,
        run: (item: T, index: number) => R | Promise<R>,
        isStale: () => boolean = () => false
    ): Promise<R[] | null> => {
        if (!batchSize) return settleAll(items, run);
        const results: R[] = [];
        // Each yield costs a render and whatever else is queued (tens of ms): cheap batches share one
        let lastYield = performance.now();
        for (let start = 0; start < items.length; start += batchSize) {
            if (isStale()) return null;
            const batch = items.slice(start, start + batchSize);
            results.push(...(await settleAll(batch, (item, i) => run(item, start + i))));
            const overBudget = !batchTimeBudgetMs || performance.now() - lastYield >= batchTimeBudgetMs;
            if (start + batchSize < items.length && overBudget) {
                getCanvas().requestRenderAll();
                await yieldToBrowser();
                lastYield = performance.now();
            }
        }
        return results;
    };

    // Removed, or replaced by a reload with the same uuids (another preview): its replicas are someone else's now
    const isOnCanvas = (origin: PatternElement) => getCanvas().getObjects().includes(origin as never);

    // A newer resync of the same origin supersedes the one still going through its batches, and so does its removal
    const syncGenerations = new Map<string, number>();
    const startSyncGeneration = (origin: PatternElement, batchSize: number | undefined) => {
        if (!batchSize) return () => !origin.pattern || !isOnCanvas(origin);
        const uuid = origin.uuid as string;
        const generation = (syncGenerations.get(uuid) ?? 0) + 1;
        syncGenerations.set(uuid, generation);
        return () => syncGenerations.get(uuid) !== generation || !origin.pattern || !isOnCanvas(origin);
    };

    // Batched builds link their replicas only once every batch is done: a resync meanwhile would build them again
    const buildsInProgress = new Map<string, Promise<void>>();
    const trackBuild = async (origin: PatternElement, batchSize: number | undefined, build: () => Promise<void>) => {
        if (!batchSize) return build();
        const uuid = origin.uuid as string;
        const done = build();
        const settled = done.catch(() => undefined);
        buildsInProgress.set(uuid, settled);
        try {
            await done;
        } finally {
            if (buildsInProgress.get(uuid) === settled) buildsInProgress.delete(uuid);
        }
    };
    const waitForBuilds = async (origin: PatternElement) => {
        const uuid = origin.uuid as string;
        for (let build = buildsInProgress.get(uuid); build; build = buildsInProgress.get(uuid)) await build;
    };

    const getReplicas = (origin: PatternElement) =>
        getPatternReplicaObjects(getCanvas(), ProductService, origin.uuid as string);

    /**
     * Puts `stacked` right above `anchor`, in order; only what was above the anchor moves up, by the least that makes
     *  room. Every level of the layers tree keeps its order: the storefront sorts all elements by zIndex as one list.
     *  `tiesAbove` is for an anchor added back: it's drawn last whatever its place, and a tie took its zIndex meanwhile
     */
    const stackAbove = (
        canvas: CanvasEx,
        anchor: PatternElement,
        stacked: PatternElement[],
        { tiesAbove = false }: { tiesAbove?: boolean } = {}
    ) => {
        const objects = canvas.getObjects() as PatternElement[];
        // Groups and page EPS share the zIndex space of the elements in the layers tree
        const containers: { zIndex: number }[] = [...(canvas.elementsGroups ?? []), ...(canvas.elementsPageEps ?? [])];
        const own = new Set<PatternElement>([anchor, ...stacked]);
        const anchorIndex = objects.indexOf(anchor);
        // Otherwise a tie is above when it's drawn after the anchor
        const isAbove = (zIndex: number, index: number) =>
            zIndex > anchor.zIndex || (zIndex === anchor.zIndex && (tiesAbove || index > anchorIndex));
        const above = objects.filter((o, i) => !own.has(o) && isAbove(o.zIndex, i));
        const containersAbove = containers.filter(
            (c) => c.zIndex > anchor.zIndex || (tiesAbove && c.zIndex === anchor.zIndex)
        );
        stacked.forEach((object, i) => object.set('zIndex', anchor.zIndex + 1 + i));
        const lowestAbove = Math.min(...above.map((o) => o.zIndex), ...containersAbove.map((c) => c.zIndex));
        const shift = Math.max(0, anchor.zIndex + stacked.length + 1 - lowestAbove);
        if (shift > 0) {
            for (const object of above) {
                object.set('zIndex', object.zIndex + shift);
                if ($scope.editMode && !object.isPatternReplica) object.setEditedProp?.('zIndex');
            }
            for (const container of containersAbove) container.zIndex += shift;
        }
    };

    /** The replicas built and linked, in cell order */
    const createReplicasForCells = async (
        origin: PatternElement,
        config: PatternConfig,
        cells: PatternCell[],
        reuseUuids: (string | undefined)[]
    ): Promise<PatternElement[]> => {
        const canvas = getCanvas();
        const service = getReplicaService(origin);
        let built: PatternElement[] = [];
        await trackBuild(origin, service.batchSize, async () => {
            try {
                built =
                    (await runInBatches(cells, service, (cell, i) => {
                        const pos = computeCellPosition(origin, config, cell);
                        return service.buildPatternReplica(canvas, origin, cell, {
                            uuid: reuseUuids[i],
                            left: pos.left,
                            top: pos.top,
                            zIndex: origin.zIndex + 1,
                            editMode: $scope.editMode,
                            ...(pos.transform && { transform: pos.transform })
                        });
                    })) ?? [];
            } catch (error) {
                // Links come once every build is done: what was built before the failure would stay as loose elements
                const linked = new Set(getReplicas(origin));
                const orphans = canvas
                    .getObjects()
                    .filter(
                        (o) =>
                            (o as PatternElement).isPatternReplica &&
                            (o as PatternElement).patternOriginUuid === origin.uuid &&
                            !linked.has(o as PatternElement)
                    );
                orphans.forEach((orphan) => canvas.remove(orphan));
                throw error;
            }
            // Linked after every build resolves so the link order keeps matching the cell order
            built.forEach((replica) =>
                ProductService.addPatternElementsLink(origin.uuid as string, replica.uuid as string, service.elementType)
            );
        });
        return built;
    };

    const reconcileReplicas = async (
        origin: PatternElement,
        config: PatternConfig,
        cells: PatternCell[],
        isStale: () => boolean = () => false
    ) => {
        const canvas = getCanvas();
        const service = getReplicaService(origin);
        const replicas = getReplicas(origin);
        const shared = replicas.slice(0, cells.length);
        const synced = await runInBatches(
            shared,
            service,
            (replica, i) => {
                replica.patternCell = { row: cells[i].row, col: cells[i].col };
                if (!followsOrigin(replica)) return;
                const pos = computeCellPosition(origin, config, cells[i]);
                return service.syncPatternReplica(canvas, origin, replica, pos);
            },
            isStale
        );
        // Stale after its last batch too (e.g. removePattern meanwhile): creating replicas would bring the pattern back
        if (!synced || isStale()) return;
        if (cells.length > replicas.length) {
            const added = await createReplicasForCells(origin, config, cells.slice(replicas.length), []);
            // The replicas there are stay where they are (only create and refresh fill restack): new ones go on top
            const canvasObjects = canvas.getObjects() as PatternElement[];
            stackAbove(canvas, getTopmost([origin, ...replicas], canvasObjects), added);
        } else if (replicas.length > cells.length) {
            for (const extra of replicas.slice(cells.length)) {
                ProductService.removePatternElementsLinkByReplica(extra.uuid as string);
                canvas.remove(extra);
            }
        }
    };

    const centerOrigin = (canvas: CanvasEx, origin: PatternElement) => {
        const center = canvas.getCenter();
        origin.set({ left: center.left, top: center.top });
        origin.setCoords();
    };

    /** Same delta for every origin, so centering the set keeps the arrangement the user made */
    const centerSet = (canvas: CanvasEx, origins: PatternElement[]) => {
        const center = canvas.getCenter();
        const bounds = getUnionBounds(origins);
        const dx = center.left - bounds.centerX;
        const dy = center.top - bounds.centerY;
        for (const origin of origins) {
            origin.set({ left: (origin.left ?? 0) + dx, top: (origin.top ?? 0) + dy });
            origin.setCoords();
        }
    };

    /**
     * Each pattern goes where its origin is, replicas right above it; only what was above moves up. The origin is the
     *  anchor because it's the only one the user can move: locked replicas end up apart only as a side effect
     */
    // After the builds: the layers panel renumbers mid-build, and ours ends up as the last call into its debounce
    const layoutPatternsInPlace = (
        canvas: CanvasEx,
        origins: PatternElement[],
        options: { tiesAbove?: boolean } = {}
    ) => {
        for (const origin of [...origins].sort((a, b) => a.zIndex - b.zIndex)) {
            stackAbove(canvas, origin, getReplicas(origin), options);
        }
        updateElementsOrderFromZIndex(canvas);
    };

    // The widest reach wins: a narrow origin would otherwise stop a ring short of one that still pokes into the page
    const getSharedFillExtent = (canvas: CanvasEx, origins: PatternElement[], configs: PatternConfig[]) => {
        const artwork = getArtworkBounds(canvas);
        const extents = origins.map((origin, i) =>
            computeFillExtent(origin, configs[i], artwork.width, artwork.height)
        );
        return {
            rows: Math.max(...extents.map((extent) => extent.rows)),
            cols: Math.max(...extents.map((extent) => extent.cols))
        };
    };

    /** One independent pattern per origin, all repeating at the set's size so the set's copies touch */
    const createPatternFromSelection = async (origins: PatternElement[]) => {
        if (!origins.length) return;
        const canvas = getCanvas();
        centerSet(canvas, origins);

        const set = getUnionBounds(origins);
        const spacings = origins.map((origin) => ({
            ...PATTERN_DEFAULTS,
            spacingX: spacingForRepeat(getAbsoluteSize(origin).width, set.width),
            spacingY: spacingForRepeat(getAbsoluteSize(origin).height, set.height)
        }));
        const extent = getSharedFillExtent(canvas, origins, spacings);
        const configs: PatternConfig[] = spacings.map((config) => ({ ...config, ...extent }));
        try {
            for (const [i, origin] of origins.entries()) {
                const { pattern, patternConfig } = origin;
                // Flagged right before its build: a resync of a flagged origin without replicas would build them twice
                origin.set('pattern', true);
                origin.set('patternConfig', configs[i]);
                markEdited(origin, ['pattern', 'patternConfig']);
                try {
                    await createReplicasForCells(origin, configs[i], enumerateCells(configs[i]), []);
                } catch (error) {
                    // Its replicas are gone already: flagged, it would be a pattern with nothing to show for it
                    origin.set('pattern', pattern);
                    origin.set('patternConfig', patternConfig);
                    throw error;
                }
            }
        } finally {
            layoutPatternsInPlace(canvas, origins.filter((origin) => origin.pattern));
            canvas.requestRenderAll();
        }
    };

    /** A value set on several patterns at once, converted to each one's own spacing/offset */
    const updateSharedRepeat = (origins: PatternElement[], change: SharedRepeatChange) => {
        for (const origin of origins) {
            if (!origin.pattern || !origin.patternConfig) continue;
            origin.set('patternConfig', applySharedRepeat(origin, origin.patternConfig, change));
            markEdited(origin, ['patternConfig']);
            repositionReplicas(origin);
        }
    };

    const createPattern = async (origin: PatternElement) => {
        const canvas = getCanvas();
        centerOrigin(canvas, origin);

        const config: PatternConfig = { ...PATTERN_DEFAULTS };
        const bounds = getArtworkBounds(canvas);
        const extent = computeFillExtent(origin, config, bounds.width, bounds.height);
        config.rows = extent.rows;
        config.cols = extent.cols;

        const { pattern, patternConfig } = origin;
        origin.set('pattern', true);
        origin.set('patternConfig', config);
        markEdited(origin, ['pattern', 'patternConfig']);

        try {
            await createReplicasForCells(origin, config, enumerateCells(config), []);
        } catch (error) {
            // Its replicas are gone already: flagged, it would be a pattern with nothing to show for it
            origin.set('pattern', pattern);
            origin.set('patternConfig', patternConfig);
            throw error;
        } finally {
            if (origin.pattern) layoutPatternsInPlace(canvas, [origin]);
            canvas.requestRenderAll();
        }
        canvas.setActiveObject(origin);
    };

    const repositionReplicasNow = (origin: PatternElement, resize: boolean) => {
        const canvas = getCanvas();
        const config = origin.patternConfig;
        if (!config) return;

        const service = getReplicaService(origin);
        const cells = enumerateCells(config);
        const replicas = getReplicas(origin);
        const count = Math.min(cells.length, replicas.length);
        for (let i = 0; i < count; i++) {
            replicas[i].patternCell = { row: cells[i].row, col: cells[i].col };
            if (!followsOrigin(replicas[i])) continue;
            const pos = computeCellPosition(origin, config, cells[i]);
            service.repositionPatternReplica(origin, replicas[i], pos, resize);
        }
        canvas.requestRenderAll();
    };

    let repositionFrame: number | null = null;
    // Per origin: a multi-selection repositions several in the same frame
    const pendingReposition = new Map<PatternElement, boolean>();
    const repositionReplicas = (origin: PatternElement, options: { resize?: boolean } = {}) => {
        pendingReposition.set(origin, !!pendingReposition.get(origin) || !!options.resize);
        if (repositionFrame !== null) return;
        repositionFrame = requestAnimationFrame(() => {
            repositionFrame = null;
            const pending = Array.from(pendingReposition);
            pendingReposition.clear();
            for (const [originToSync, resize] of pending) repositionReplicasNow(originToSync, resize);
        });
    };

    const resyncReplicas = async (origin: PatternElement) => {
        if (buildsInProgress.has(origin.uuid as string)) await waitForBuilds(origin);
        const canvas = getCanvas();
        const config = origin.patternConfig;
        if (!config || !isOnCanvas(origin)) return;

        const service = getReplicaService(origin);
        const isStale = startSyncGeneration(origin, service.batchSize);
        const cells = enumerateCells(config);
        const replicas = getReplicas(origin);

        // zIndex is left as is: replicas apart from a moved origin only go back to it on create and refresh fill
        if (cells.length !== replicas.length) {
            await reconcileReplicas(origin, config, cells, isStale);
        } else {
            await runInBatches(
                replicas,
                service,
                (replica, i) => {
                    replica.patternCell = { row: cells[i].row, col: cells[i].col };
                    if (!followsOrigin(replica)) return;
                    const pos = computeCellPosition(origin, config, cells[i]);
                    return service.syncPatternReplica(canvas, origin, replica, pos);
                },
                isStale
            );
        }
        if (isStale()) return;
        updateElementsOrderFromZIndex(canvas);
        canvas.requestRenderAll();
    };

    /** `extent` is shared by patterns refilled together, so every cell gets all of them */
    const refillPage = async (origin: PatternElement, sharedExtent?: { rows: number; cols: number }) => {
        const canvas = getCanvas();
        const config: PatternConfig = { ...(origin.patternConfig || PATTERN_DEFAULTS) };
        const bounds = getArtworkBounds(canvas);
        const extent = sharedExtent ?? computeFillExtent(origin, config, bounds.width, bounds.height);
        config.rows = extent.rows;
        config.cols = extent.cols;
        origin.set('patternConfig', config);
        markEdited(origin, ['patternConfig']);

        const isStale = startSyncGeneration(origin, getReplicaService(origin).batchSize);
        await reconcileReplicas(origin, config, enumerateCells(config), isStale);
        if (isStale()) return;
        // Refilling can add cells: whatever was above the pattern makes room for them
        layoutPatternsInPlace(canvas, [origin]);
        canvas.requestRenderAll();
    };

    const refreshFill = async (origin: PatternElement) => {
        if (buildsInProgress.has(origin.uuid as string)) await waitForBuilds(origin);
        if (!isOnCanvas(origin)) return;
        centerOrigin(getCanvas(), origin);
        await refillPage(origin);
    };

    /** Several refreshed at once: centering each would undo how they're arranged, so the set is centered */
    const refreshSelectionFill = async (origins: PatternElement[]) => {
        for (const origin of origins) {
            if (buildsInProgress.has(origin.uuid as string)) await waitForBuilds(origin);
        }
        const onCanvas = origins.filter(isOnCanvas);
        if (!onCanvas.length) return;
        const canvas = getCanvas();
        centerSet(canvas, onCanvas);
        const extent = getSharedFillExtent(
            canvas,
            onCanvas,
            onCanvas.map((origin) => origin.patternConfig || PATTERN_DEFAULTS)
        );
        for (const origin of onCanvas) await refillPage(origin, extent);
        // Once more for all of them: the layers panel may have renumbered while the later ones were built
        layoutPatternsInPlace(canvas, onCanvas.filter(isOnCanvas));
        canvas.requestRenderAll();
    };

    const updateConfig = (origin: PatternElement, partial: Partial<PatternConfig>) => {
        const current = origin.patternConfig || PATTERN_DEFAULTS;
        const config: PatternConfig = { ...current, ...partial };
        origin.set('patternConfig', config);
        markEdited(origin, ['patternConfig']);
        repositionReplicas(origin);
    };

    const removeReplicasOf = (originUuid: string) => {
        const canvas = getCanvas();
        for (const replica of getPatternReplicaObjects(canvas, ProductService, originUuid)) canvas.remove(replica);
        ProductService.removePatternElementsLinks(originUuid);
    };

    const removePattern = (origin: PatternElement) => {
        const canvas = getCanvas();
        removeReplicasOf(origin.uuid as string);

        origin.set('pattern', false);
        origin.set('patternConfig', null);
        markEdited(origin, ['pattern', 'patternConfig']);
        updateElementsOrderFromZIndex(canvas);
        canvas.requestRenderAll();
    };

    // Replicas have no undo entries of their own: an origin taken off the canvas keeps them to come back with them
    const detachedPatterns = new WeakMap<PatternElement, PatternElement[]>();

    /** The origin left the canvas (deleted, or its add undone): its replicas and links go, kept for reattachPattern */
    const detachPattern = (origin?: PatternElement | null) => {
        // A reload (preview switch, new background) clears the canvas through removals: the pattern stays in the product
        if (getCanvas().isClearing) return;
        if (!origin || !isPatternOriginType(origin) || !origin.pattern) return;
        detachedPatterns.set(origin, getReplicas(origin));
        removeReplicasOf(origin.uuid as string);
    };

    /** The same origin is back (redo of its add, undo of its removal): so are its replicas, in their link order */
    const reattachPattern = (origin?: PatternElement | null) => {
        const replicas = origin ? detachedPatterns.get(origin) : undefined;
        if (!origin || !replicas) return;
        detachedPatterns.delete(origin);
        if (!origin.pattern || getReplicas(origin).length) return;
        const canvas = getCanvas();
        const { elementType } = getReplicaService(origin);
        for (const replica of replicas) {
            canvas.addWithoutHistory(replica);
            ProductService.addPatternElementsLink(origin.uuid as string, replica.uuid as string, elementType);
        }
        // The layers panel renumbered while it was gone: whatever has its zIndex or more now was above it, and stays so
        layoutPatternsInPlace(canvas, [origin], { tiesAbove: true });
        repositionReplicas(origin, { resize: true });
    };

    /** Drops the replicas/links of deleted elements. Only uuids survive the removal, so links tell origins apart. */
    const cleanupDeletedPatterns = (deleted: { uuid?: string }[] | null | undefined) => {
        if (!deleted?.length) return;
        for (const { uuid } of deleted) {
            if (!uuid) continue;
            if (ProductService.getPatternReplicaLinks(uuid).length) {
                removeReplicasOf(uuid);
            } else {
                ProductService.removePatternElementsLinkByReplica(uuid);
            }
        }
        updateElementsOrderFromZIndex(getCanvas());
    };

    return {
        createPattern,
        createPatternFromSelection,
        updateSharedRepeat,
        refreshSelectionFill,
        repositionReplicas,
        resyncReplicas,
        refreshFill,
        updateConfig,
        removePattern,
        cleanupDeletedPatterns,
        detachPattern,
        reattachPattern
    };
};
